"use client";

import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleHelp } from "lucide-react";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import {
  type IFilter,
  BookingDateModalMobile,
} from "@/src/shared/features/search";
import type { IListing } from "@/src/shared/types/listing";
import type { BlockedDate } from "@/src/shared/types/blockedDate";
import { listingService } from "@/src/features/listing/services";
import { confirmPayService } from "@/src/features/listing/confirmPay";
import { useConfirmAndPay } from "@/src/features/listing/hooks/useConfirmAndPay";
import SelectPaymentMethodPage from "@/src/features/listing/components/ConfirmPay/SelectPaymentMethodPage";
import PriceBreakdownSheet from "@/src/features/listing/components/ConfirmPay/PriceBreakdownSheet";
import PaymentGatewayCard from "@/src/features/listing/components/ConfirmPay/PaymentGatewayCard";
import ChangeGuestsSheet from "@/src/features/listing/components/ConfirmPay/ChangeGuestsSheet";
import ConfirmPayPrimaryButton from "@/src/features/listing/components/ConfirmPay/ConfirmPayPrimaryButton";
import ConfirmPayRadio from "@/src/features/listing/components/ConfirmPay/ConfirmPayRadio";
import ConfirmPayTopBar from "@/src/features/listing/components/ConfirmPay/ConfirmPayTopBar";
import Hairline from "@/src/features/listing/components/ConfirmPay/Hairline";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IConfirmAndPayPageProps {
  listing: IListing;
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  blockedDates: BlockedDate[];
  checkoutOnlyDates: string[];
  onStayChanged: () => void;
  onClose: () => void;
}

type Overlay = "dates" | "guests" | "breakdown" | "method" | null;

const SummaryRow: React.FC<{
  label: string;
  value: string;
  action: string;
  onAction: () => void;
}> = ({ label, value, action, onAction }) => (
  <div className="flex items-center gap-3 py-4">
    <div className="min-w-0 flex-1">
      <p className="text-16 font-medium leading-5 text-ink">{label}</p>
      <p className="mt-0.5 text-16 leading-5 text-ink-secondary">{value}</p>
    </div>
    <button
      type="button"
      onClick={onAction}
      className="flex h-8 shrink-0 items-center rounded-full bg-[#F7F7F7] px-4 text-14 font-semibold leading-5 text-ink"
    >
      {action}
    </button>
  </div>
);

const PriceRow: React.FC<{
  label: string;
  amount: string;
  isDiscount?: boolean;
}> = ({ label, amount, isDiscount = false }) => (
  <div className="flex items-center justify-between py-2 text-16 leading-6 text-ink">
    <span>{label}</span>
    <span className={isDiscount ? "text-success-earn" : ""}>{amount}</span>
  </div>
);

const TotalRow: React.FC<{
  label?: string;
  amount: string;
  isEmphasized?: boolean;
}> = ({ label = "Total BDT", amount, isEmphasized = true }) => (
  <div
    className={`flex items-center justify-between border-t border-[#EBEBEB] py-4 text-16 leading-6 text-ink ${isEmphasized ? "font-medium" : ""}`}
  >
    <span>{label}</span>
    <span>{amount}</span>
  </div>
);

const GuestFavourite: React.FC = () => (
  <span className="inline-flex items-center gap-[3.5px]">
    <img
      src="/icons/confirm-pay/confirm_pay_laurel.svg"
      alt=""
      className="h-4 w-[10.667px]"
    />
    <span className="text-center origin-center scale-75 text-8 font-bold leading-[9px] text-ink-black">
      Guest
      <br />
      favorite
    </span>
    <img
      src="/icons/confirm-pay/confirm_pay_laurel.svg"
      alt=""
      className="h-4 w-[10.667px] -scale-x-100"
    />
  </span>
);

/** The app's `ConfirmAndPayPage`, pushed over the listing on a phone for an instant booking. */
const ConfirmAndPayPage: React.FC<IConfirmAndPayPageProps> = ({
  listing,
  filters,
  setFilters,
  blockedDates,
  checkoutOnlyDates,
  onStayChanged,
  onClose,
}) => {
  const [overlay, setOverlay] = useState<Overlay>(null);
  useLockBodyScroll();
  const checkout = useConfirmAndPay({
    listing,
    filters,
    isOpen: true,
    onNeedDates: () => setOverlay("dates"),
  });
  const { totals, wallet } = checkout;
  const isGuestFavourite = !!listingService.guestFavouriteBadge(listing.badges);
  const hasReviews = (listing.reviews_count ?? 0) > 0;
  const cancellation = confirmPayService.cancellationSummary(
    listing.cancellation,
    filters.checkIn,
    new Date(),
  );
  const totalPriceValue = checkout.isPriceLoading
    ? "Calculating…"
    : totals
      ? `${confirmPayService.taka(totals.total)} BDT`
      : "Add dates to see price";

  const changeMethodLink = (
    <button
      type="button"
      onClick={() => setOverlay("method")}
      className="shrink-0 text-12 font-semibold text-ink-secondary underline"
    >
      Change
    </button>
  );
  const gatewayCard = (
    <PaymentGatewayCard
      gateway={checkout.gatewayChoice.gateway}
      savedBkashNumber={checkout.gatewayChoice.savedBkashNumber}
    />
  );

  const handleBack = () => {
    if (!checkout.isPaying) onClose();
  };

  const handleConfirm = async () => {
    if (checkout.needsMethodChoice) {
      setOverlay("method");
      return;
    }
    await checkout.pay();
  };

  const commitStay = (change: Partial<IFilter>) => {
    setFilters((prev) => ({ ...prev, ...change }));
    setOverlay(null);
    onStayChanged();
  };

  return (
    <>
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={IOS_PUSH}
        role="dialog"
        aria-modal
        aria-label="Confirm and pay"
        className="fixed inset-0 z-50 flex flex-col bg-white pt-[env(safe-area-inset-top)] md:hidden"
      >
        <ConfirmPayTopBar title="Confirm and pay" onBack={handleBack} />

        <div
          className={`scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 ${checkout.isPaying ? "pointer-events-none" : ""}`}
        >
          <section className="rounded-xl border border-[#EBEBEB] p-4">
            <div className="flex items-start gap-3 pb-4">
              <img
                src={listing.images?.[0]?.url ?? ""}
                alt=""
                className="h-16 w-16 shrink-0 rounded-xl bg-[#F7F7F7] object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-16 font-semibold leading-5 text-ink">
                  {listing.title}
                </p>
                {(hasReviews || isGuestFavourite) && (
                  <div className="mt-2 flex items-center gap-3">
                    {hasReviews && (
                      <span className="inline-flex items-center gap-1 text-12 font-semibold leading-4 text-ink">
                        <img
                          src="/icons/confirm-pay/listing_rating_star.svg"
                          alt=""
                          className="h-3.5 w-3.5"
                        />
                        {listing.reviews_avg} ({listing.reviews_count})
                      </span>
                    )}
                    {isGuestFavourite && <GuestFavourite />}
                  </div>
                )}
              </div>
            </div>
            <Hairline />
            <SummaryRow
              label="Dates"
              value={
                confirmPayService.stayDates(
                  filters.checkIn,
                  filters.checkOut,
                ) ?? "Add dates"
              }
              action="Change"
              onAction={() => setOverlay("dates")}
            />
            <Hairline />
            <SummaryRow
              label="Guests"
              value={confirmPayService.guestsBreakdown(filters.guests)}
              action="Change"
              onAction={() => setOverlay("guests")}
            />
            <Hairline />
            <SummaryRow
              label="Total price"
              value={totalPriceValue}
              action="Details"
              onAction={() => {
                if (totals) setOverlay("breakdown");
              }}
            />
            <Hairline />
            <div className="pt-4">
              <p className="text-16 leading-5 text-ink">{cancellation.title}</p>
              {cancellation.body && (
                <p className="mt-1 text-16 leading-5 text-ink-secondary">
                  {cancellation.body}
                </p>
              )}
            </div>
          </section>

          {(wallet.isAvailable || checkout.gatewayChoice.hasSavedBkash) && (
            <section className="pt-6">
              {wallet.isAvailable ? (
                <>
                  <h2 className="text-18 font-semibold leading-6 text-ink">
                    Payment method
                  </h2>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={wallet.isOn}
                    aria-label="Travela Wallet"
                    onClick={() => wallet.toggle(!wallet.isOn)}
                    className={`mt-3 flex w-full items-center gap-3 rounded-xl bg-white p-4 text-left ${wallet.isOn ? "border-[1.5px] border-[#222222]" : "border border-[#EBEBEB]"}`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
                      <img
                        src="/icons/confirm-pay/wallet_icon.svg"
                        alt=""
                        className="h-5 w-5"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-16 font-semibold leading-5 text-ink">
                        Travela Wallet
                      </span>
                      <span
                        className={`mt-0.5 block text-12 leading-4 ${wallet.isOn ? "text-success-verified" : "text-ink-secondary"}`}
                      >
                        {confirmPayService.walletStatus(
                          wallet.balance,
                          wallet.walletAmount,
                        )}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={`relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors ${wallet.isOn ? "bg-[#222222]" : "bg-[#E9E9EA]"}`}
                    >
                      <span
                        className={`inline-block h-[27px] w-[27px] rounded-full bg-white shadow transition-transform ${wallet.isOn ? "translate-x-[22px]" : "translate-x-0.5"}`}
                      />
                    </span>
                  </button>
                  {wallet.isOn && wallet.heldBackNotice && (
                    <div className="mt-3 flex gap-3 rounded-xl bg-[#F7F7F7] p-4">
                      <CircleHelp
                        aria-hidden
                        className="h-5 w-5 shrink-0 text-ink"
                      />
                      <div className="min-w-0">
                        <p className="text-14 font-semibold leading-5 text-ink">
                          Why not your full balance?
                        </p>
                        <p className="mt-1 text-12 leading-4 text-ink-secondary">
                          {wallet.heldBackNotice}
                        </p>
                      </div>
                    </div>
                  )}
                  {checkout.gatewayChoice.hasSavedBkash &&
                    !wallet.isPaidByWalletOnly && (
                      <>
                        <div className="mt-3 flex items-center gap-3">
                          <p className="min-w-0 flex-1 text-12 leading-4 text-ink-secondary">
                            {confirmPayService.gatewayPrompt(
                              wallet.walletAmount,
                              wallet.remaining,
                            )}
                          </p>
                          {changeMethodLink}
                        </div>
                        <div className="mt-3">{gatewayCard}</div>
                      </>
                    )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-18 font-semibold leading-6 text-ink">
                      Payment method
                    </h2>
                    {changeMethodLink}
                  </div>
                  <div className="mt-3">{gatewayCard}</div>
                </>
              )}
            </section>
          )}

          <section className="pt-6">
            <h2 className="text-18 font-semibold leading-6 text-ink">
              Choose when to pay
            </h2>
            <div className="mt-3 overflow-hidden rounded-xl border border-[#EBEBEB]">
              <button
                type="button"
                onClick={() => checkout.selectTiming("full")}
                className="flex w-full items-center gap-3 p-4 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-14 font-semibold leading-5 text-ink">
                    {confirmPayService.fullPaymentLabel(checkout.payableTotal)}
                  </span>
                  <span className="mt-0.5 block text-12 leading-4 text-ink-secondary">
                    Pay the full amount and confirm instantly.
                  </span>
                </span>
                <ConfirmPayRadio isSelected={checkout.timing === "full"} />
              </button>
              <Hairline />
              {checkout.canPayPartially && (
                <>
                  <button
                    type="button"
                    onClick={() => checkout.selectTiming("part")}
                    className="block w-full p-4 text-left"
                  >
                    <span className="flex items-start gap-3">
                      <span className="min-w-0 flex-1 text-14 font-semibold leading-5 text-ink">
                        Pay part now, rest later
                      </span>
                      <ConfirmPayRadio
                        isSelected={checkout.timing === "part"}
                      />
                    </span>
                    <span className="mt-2.5 block text-12 text-ink-secondary">
                      {confirmPayService.partialDetail(
                        checkout.minimum,
                        checkout.lastPartialPayment,
                      )}
                    </span>
                  </button>
                  <Hairline />
                </>
              )}
            </div>
          </section>

          {!checkout.isPriceLoading && totals && (
            <section className="pt-6">
              <Hairline />
              <h2 className="pt-6 text-18 font-semibold leading-6 text-ink">
                Price details
              </h2>
              <PriceRow
                label={confirmPayService.nightlyLine(
                  checkout.nights,
                  totals.perNightBeforeDiscount,
                )}
                amount={confirmPayService.taka(totals.base)}
              />
              {totals.discount > 0 && (
                <PriceRow
                  label={confirmPayService.discountLabel(checkout.nights)}
                  amount={`-${confirmPayService.taka(totals.discount)}`}
                  isDiscount
                />
              )}
              {wallet.walletAmount > 0 ? (
                <>
                  <TotalRow
                    label="Booking total"
                    amount={confirmPayService.groupedTaka(totals.total)}
                    isEmphasized={false}
                  />
                  <PriceRow
                    label="Paid from Travela Wallet"
                    amount={`-${confirmPayService.groupedTaka(wallet.walletAmount)}`}
                  />
                  {wallet.remaining > 0 && (
                    <TotalRow
                      label={confirmPayService.payNowLabel(
                        checkout.gatewayChoice.gateway,
                      )}
                      amount={confirmPayService.groupedTaka(wallet.remaining)}
                      isEmphasized={false}
                    />
                  )}
                </>
              ) : (
                <TotalRow amount={confirmPayService.taka(totals.total)} />
              )}
            </section>
          )}
        </div>

        <div className="bg-white pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 pb-3 pt-4">
            <ConfirmPayPrimaryButton
              label={checkout.confirmLabel}
              isLoading={checkout.isPaying}
              onClick={() => void handleConfirm()}
            />
          </div>
          <p className="px-6 pb-2 text-center text-12 leading-4 text-ink-secondary">
            By selecting the button, I agree to the{" "}
            <a
              href="/terms-conditions"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink underline"
            >
              booking terms
            </a>
            .
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {overlay === "dates" && (
          <BookingDateModalMobile
            key="confirm-dates"
            appearance="app"
            blockedDates={blockedDates}
            checkoutOnlyDates={checkoutOnlyDates}
            filters={filters}
            setFilters={setFilters}
            onBack={() => setOverlay(null)}
            onDoneDates={(dates) => {
              if (!dates.checkIn) setOverlay(null);
              else commitStay(dates);
            }}
          />
        )}
        {overlay === "guests" && (
          <ChangeGuestsSheet
            key="confirm-guests"
            guests={filters.guests}
            max={{
              adults: listing.max_guest,
              children: listing.max_child,
              infants: listing.max_infant,
            }}
            onSave={(guests) => commitStay({ guests })}
            onClose={() => setOverlay(null)}
          />
        )}
        {overlay === "breakdown" && totals && (
          <PriceBreakdownSheet
            key="confirm-breakdown"
            nightsLabel={confirmPayService.breakdownNights(
              checkout.nights,
              filters.checkIn,
              filters.checkOut,
            )}
            nights={checkout.nights}
            totals={totals}
            onClose={() => setOverlay(null)}
          />
        )}
        {overlay === "method" && (
          <SelectPaymentMethodPage
            key="confirm-method"
            choice={checkout.gatewayChoice}
            isPaying={checkout.isPaying}
            onPay={() => void checkout.pay()}
            onBack={() => {
              if (!checkout.isPaying) setOverlay(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ConfirmAndPayPage;
